/**
 * Hierarchical, searchable field picker for the rules-engine QueryBuilder
 * (NGTPA-24052).
 *
 * Trigger is shaped like a form field (Select-style) showing the current
 * selection. Popover content + search are delegated to the shared
 * `HierarchicalSearchablePopover` (same component that backs the template
 * token picker), so both call sites share the hybrid filter behavior:
 * tree-while-browsing, flat-with-breadcrumb-while-searching.
 *
 * Tree construction lives in `buildFieldTree.ts` so this file is just the
 * trigger + glue between RQB and the shared popover.
 */

import React, { useContext, useMemo } from 'react';
import type { FieldSelectorProps, OptionGroup } from 'react-querybuilder';
import { defaultPlaceholderFieldName } from 'react-querybuilder';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import Tooltip from '~/components/core/Atomic/Tooltip';
import TextField from '~/components/core/Molecules/Fields/TextField';
import HierarchicalSearchablePopover from '~/components/core/Molecules/HierarchicalSearchablePopover/HierarchicalSearchablePopover';
import { StackIcon } from '~/components/icons';
import usePopoverState from '~/components/ui/atoms/Popover/usePopoverState';
import colors from '~/theme/tailwind/colors';

import { buildFieldTree, type FieldOption } from './buildFieldTree';
import { QueryBuilderContext } from './context';
import { FIELD_TYPE } from './types';

const PLACEHOLDER_NAME = defaultPlaceholderFieldName;

const COLLECTION_FIELD_HINT = 'Matches across multiple items';

/** Follows the label of a field whose `field_type` is `collection`: picking one opens a match-mode
 *  quantifier over its items rather than a comparison. The wrapper is the labelled element — the
 *  Tooltip needs a child it can hold a ref to, and names that child from its title — so it is the
 *  one that takes `role="img"`. */
const CollectionFieldIcon: React.FC = () => (
  <Tooltip title={COLLECTION_FIELD_HINT}>
    <span role="img" aria-label={COLLECTION_FIELD_HINT} className="ml-8 inline-flex shrink-0 align-middle">
      <StackIcon size={15} iconColor={colors.slate['700']} />
    </span>
  </Tooltip>
);

/** Only the three RQB props this control actually reads, plus the presentation
 *  overrides a second instance needs. It is used both as RQB's `fieldSelector`
 *  (which supplies the full `FieldSelectorProps`) and directly by the ValueEditor's
 *  target picker, which has no RQB props to hand it — hence the `Pick`. */
export type HierarchicalFieldSelectorProps = Pick<FieldSelectorProps, 'value' | 'handleOnChange'> & {
  /** The entries to offer. Declared as what this control *reads* rather than picked off
   *  `FieldSelectorProps`: react-querybuilder v8 narrowed that prop to `FullField`, which carries
   *  a required `value` alongside `name`, and the anchor picker below feeds this control a list
   *  built from our own registry where the path is the name and there is no second spelling of
   *  it. Both shapes satisfy this one, and neither caller has to invent a key nothing here
   *  reads. */
  options: ReadonlyArray<FieldOption> | ReadonlyArray<OptionGroup<FieldOption>>;
  /** DOM id of the trigger input. Two instances in one row would otherwise collide. */
  id?: string;
  /** Trigger label, also its `aria-label`. */
  label?: string;
  /** Entries pinned above the field tree, e.g. the target picker's non-field targets. */
  leadingOptions?: ReadonlyArray<FieldOption>;
  /** A note under the trigger about the current value — the target picker's "to the minute".
   *  The "Required" marker takes its place while the cell is unset and errors are shown. */
  helperText?: string;
  triggerTestId?: string;
  searchTestId?: string;
};

const HierarchicalFieldSelector: React.FC<HierarchicalFieldSelectorProps> = ({
  value,
  options,
  handleOnChange,
  id = 'qb-field',
  label = 'Field',
  leadingOptions,
  helperText,
  triggerTestId = 'qb-field-trigger',
  searchTestId = 'qb-field-search',
}) => {
  const { shouldShowErrors } = useContext(QueryBuilderContext);
  const { triggerProps, popoverProps, isOpen, openFrom, close: closePopover } = usePopoverState();

  // RQB delivers `options` either flat or wrapped in OptionGroup<Field>[];
  // QB doesn't enable RQB's grouping so we expect flat, but unwrap defensively.
  const flatOptions = useMemo<FieldOption[]>(() => {
    const list = (options as ReadonlyArray<unknown>).flatMap((entry) => {
      const maybeGroup = entry as { options?: ReadonlyArray<FieldOption> };
      return maybeGroup.options ? Array.from(maybeGroup.options) : [entry as FieldOption];
    });
    return list.filter((o) => o.name !== PLACEHOLDER_NAME);
  }, [options]);

  const tree = useMemo(() => buildFieldTree(flatOptions), [flatOptions]);
  // Keyed on the catalog's `field_type`, so every collection a provider publishes is marked with no
  // list of paths here.
  const collectionNames = useMemo(
    () => new Set(flatOptions.filter((o) => o.field_type === FIELD_TYPE.Collection).map((o) => o.name)),
    [flatOptions]
  );

  const leading = useMemo(
    () => (leadingOptions ?? []).map((opt) => ({ id: opt.name, label: opt.label })),
    [leadingOptions]
  );

  const labelByName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const opt of [...(leadingOptions ?? []), ...flatOptions]) {
      map[opt.name] = opt.label;
    }
    return map;
  }, [flatOptions, leadingOptions]);

  const isUnset = !value || value === PLACEHOLDER_NAME;
  const currentLabel = isUnset ? '' : (labelByName[value!] ?? value!);
  const showError = shouldShowErrors && isUnset;

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={label}
        {...triggerProps}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openFrom(e);
          }
        }}
        // Mirror MUI Select's native cursor + caret affordance: pointer cursor
        // signals the whole row is clickable (the inner read-only input also
        // gets pointer via the wrapper). Without this the trigger looks like a
        // disabled text field.
        className="cursor-pointer [&_.MuiInputBase-input]:cursor-pointer"
        data-testid={triggerTestId}
      >
        <TextField
          id={id}
          label={label}
          value={currentLabel}
          onChange={() => undefined}
          fullWidth
          error={showError}
          helperText={showError ? 'Required' : helperText}
          // Hide the text caret and skip the input in the tab order — the
          // wrapper div is the real interactive control. Without these the
          // input still shows a blinking caret after a value is selected
          // even though it's not editable.
          inputProps={{ tabIndex: -1, style: { caretColor: 'transparent', cursor: 'pointer' } }}
          InputProps={{
            readOnly: true,
            // Same triangle MUI Select uses, sized to match SingleSelectField.
            // Rotate 180° while open so the trigger reads as "expanded".
            endAdornment: (
              <ArrowDropDownIcon className={`text-slate-700 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            ),
          }}
        />
      </div>
      <HierarchicalSearchablePopover
        {...popoverProps}
        options={tree}
        leadingOptions={leading}
        onSelect={(token) => {
          handleOnChange(token.id);
          closePopover();
        }}
        label={label}
        searchTestId={searchTestId}
        // The field this cell already holds, so opening the picker lands on it rather than at the
        // top of a tree the current value may be two collapsed groups down in. `value` is the
        // option id in both shapes this control accepts — the registry path for a catalog field,
        // the element name for a collection sub-leaf — which is the same key the tree is built on.
        selectedId={value || undefined}
        renderLeafAdornment={(id) => (collectionNames.has(id) ? <CollectionFieldIcon /> : null)}
      />
    </>
  );
};

export default HierarchicalFieldSelector;
